import { useState, useEffect, useCallback } from "react";
import FiscalOrderDialog, { isFiscalAdmin } from "@/components/FiscalOrderDialog";
import { OrderDialog } from "./OrdersPage";
import FiscalStatus, { useFiscalStatuses } from "@/components/FiscalStatus";
import { cents, newestOrder } from "@/lib/orderPayload";
import {
  CalendarIcon, DollarSign, ChevronDown, X, 
  ReceiptText, Truck, TrendingUp, Wallet, Printer, Pencil
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";

const STATUS_MAP = {
  confirmado: { label: "Confirmado", cls: "bg-green-900/50 text-green-100 border-transparent" },
  pendente: { label: "Pendente", cls: "bg-yellow-900/50 text-yellow-100 border-transparent" },
  preparando: { label: "Preparando", cls: "bg-blue-900/50 text-blue-100 border-transparent" },
  entregue: { label: "Entregue", cls: "bg-emerald-900/50 text-emerald-100 border-transparent" },
  cancelado: { label: "Cancelado", cls: "bg-red-900/50 text-red-100 border-transparent" },
};

function fmtBRL(v) {
  const num = parseFloat(v) || 0;
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
}

function fmtDay(dateStr) {
  if (!dateStr) return "-";
  try { return format(new Date(dateStr + "T12:00:00"), "EEEE, dd 'de' MMMM", { locale: ptBR }); }
  catch { return dateStr; }
}

function formatCalDate(d) {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Função que injeta o HTML de forma invisível e aciona a impressora
const imprimirComanda = (htmlContent) => {
  if (!htmlContent) {
    alert("Este pedido não possui uma comanda em HTML.");
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  iframe.contentDocument.write(htmlContent);
  iframe.contentDocument.close();

  iframe.contentWindow.focus();
  
  setTimeout(() => {
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 500);
};

export default function FatPage() {
  const [editingOrder, setEditingOrder] = useState(null);
  const [fiscalOrder, setFiscalOrder] = useState(null);
  const [ordersByDate, setOrdersByDate] = useState({});
  const [summary, setSummary] = useState({ days:[], grand_total: 0, grand_count: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingDays, setLoadingDays] = useState(new Set());
  
  // Filtros de Data
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // Controle das Gavetas (Accordion)
  const [expandedDay, setExpandedDay] = useState(null);
  const fiscalStatuses = useFiscalStatuses(ordersByDate[expandedDay] || [], isFiscalAdmin());

  const handleOrderSaved = saved => {
    const previous = Object.values(ordersByDate).flat().find(order => order.id === saved.id);
    if (!previous || previous.versao > saved.versao) return;
    setOrdersByDate(current => Object.fromEntries(Object.entries(current).map(([day, orders]) => [day,
      orders.map(order => order.id === saved.id ? newestOrder(order, saved) : order)])));
    const difference = cents(saved.Valor_Total) - cents(previous.Valor_Total);
    if (difference) {
      const day = Object.entries(ordersByDate).find(([, orders]) => orders.some(order => order.id === saved.id))?.[0];
      setSummary(current => ({ ...current, grand_total: (cents(current.grand_total) + difference) / 100,
        days: current.days.map(item => item.date === day ? { ...item, total: (cents(item.total) + difference) / 100 } : item) }));
    }
  };

  // Converte as datas do calendário para texto (ex: "2026-02-26")
  const dateFromStr = dateFrom ? formatCalDate(dateFrom) : null;
  const dateToStr = dateTo ? formatCalDate(dateTo) : null;

  // O resumo e calculado no banco e retorna apenas uma linha por dia.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFromStr) params.date_from = dateFromStr;
      if (dateToStr) params.date_to = dateToStr;
      const response = await api.get("/orders/summary", { params });
      setSummary(response.data);
      setOrdersByDate({});
      setExpandedDay(null);
    } catch (err) { 
      console.error("Erro ao buscar faturamento:", err); 
    } finally { 
      setLoading(false); 
    }
  }, [dateFromStr, dateToStr]);

  // Dispara a busca quando a página abre ou quando o filtro muda
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const clearFilters = () => { 
    setDateFrom(null); 
    setDateTo(null); 
  };

  const toggleDay = async (day) => {
    if (expandedDay === day) {
      setExpandedDay(null);
      return;
    }

    setExpandedDay(day);
    if (ordersByDate[day]) return;

    setLoadingDays(current => new Set(current).add(day));
    try {
      const response = await api.get("/orders/history", {
        params: { date_from: day, date_to: day, limit: 200 }
      });
      setOrdersByDate(current => ({ ...current, [day]: response.data.items }));
    } catch (err) {
      console.error("Erro ao buscar pedidos do dia:", err);
      setOrdersByDate(current => ({ ...current, [day]: [] }));
    } finally {
      setLoadingDays(current => {
        const next = new Set(current);
        next.delete(day);
        return next;
      });
    }
  };

  const handlePrint = async (order) => {
    try {
      const response = await api.get(`/orders/${order.id}/print`);
      imprimirComanda(response.data.html_comanda);
    } catch (err) {
      console.error("Erro ao carregar comanda:", err);
      alert("Nao foi possivel carregar a comanda para impressao.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up stagger-1">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: "Oswald, sans-serif" }}>
            Faturamento
          </h1>
          <p className="text-muted-foreground mt-1">Histórico de pedidos e receitas consolidadas</p>
        </div>
      </div>

      {/* Filtros de Data */}
      <div className="flex flex-wrap items-end gap-3 animate-fade-in-up stagger-2 bg-card p-4 rounded-lg border border-border card-shadow">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Data Início</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" className="w-44 justify-start gap-2 h-10 text-sm font-normal">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                {dateFrom ? format(dateFrom, "dd/MM/yyyy") : "Selecionar"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} locale={ptBR} initialFocus className="rounded-md" />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Data Fim</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" className="w-44 justify-start gap-2 h-10 text-sm font-normal">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                {dateTo ? format(dateTo, "dd/MM/yyyy") : "Selecionar"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} locale={ptBR} initialFocus className="rounded-md" />
            </PopoverContent>
          </Popover>
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground gap-1 h-10">
            <X className="w-3.5 h-3.5" /> Limpar Filtros
          </Button>
        )}
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-in-up stagger-3">
        <Card className="bg-card border-border card-shadow">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-green-900/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Receita Total</p>
              <p className="text-2xl font-bold text-green-400" style={{ fontFamily: "Oswald, sans-serif" }}>
                {fmtBRL(summary.grand_total)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-shadow">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-primary/15 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Volume de Pedidos</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "Oswald, sans-serif" }}>
                {summary.grand_count}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-shadow">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-accent/15 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Dias Operados</p>
              <p className="text-2xl font-bold text-accent" style={{ fontFamily: "Oswald, sans-serif" }}>
                {summary.days.length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator className="bg-border" />

      {/* Tabela Agrupada por Dias (Gavetas) */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-card border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      ) : summary.days.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground animate-fade-in-up stagger-4">
          <ReceiptText className="w-14 h-14 mx-auto mb-4 opacity-20" />
          <p className="text-lg">Nenhum dado financeiro encontrado</p>
          <p className="text-sm mt-1">Tente alterar o filtro de datas para ver o histórico.</p>
        </div>
      ) : (
        <div className="space-y-3 animate-fade-in-up stagger-4">
          {summary.days.map((day) => {
            const isExpanded = expandedDay === day.date;
            const dayOrders = ordersByDate[day.date] ||[];
            const dayLoading = loadingDays.has(day.date);
            
            return (
              <div key={day.date} className="bg-card border border-border rounded-lg overflow-hidden card-shadow">
                {/* Cabeçalho da Gaveta */}
                <button 
                  onClick={() => toggleDay(day.date)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
                      <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm capitalize">{fmtDay(day.date)}</p>
                      <p className="text-xs text-muted-foreground">{day.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{day.count} pedido{day.count !== 1 ? "s" : ""}</p>
                      <p className="text-sm font-bold text-green-400">{fmtBRL(day.total)}</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {/* Conteúdo da Gaveta */}
                {isExpanded && dayLoading && (
                  <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">
                    Carregando pedidos deste dia...
                  </div>
                )}
                {isExpanded && !dayLoading && dayOrders.length === 0 && (
                  <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">
                    Nenhum pedido encontrado neste dia.
                  </div>
                )}
                {isExpanded && !dayLoading && dayOrders.length > 0 && (
                  <div className="border-t border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Hora</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Cliente</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:table-cell">Descrição</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Pgto</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Entrega</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Total</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dayOrders.map(order => {
                          const horaPedido = order.created_at ? new Date(order.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : "-";
                          
                          return (
                            <TableRow key={order.id} className="border-b border-border hover:bg-white/[0.02] transition-colors group">
                              <TableCell className="text-xs text-muted-foreground">{horaPedido}</TableCell>
                              <TableCell>
                                <p className="text-sm font-medium truncate max-w-[120px]">{order.nomeCliente || "-"}</p>
                                <p className="text-xs text-muted-foreground">Comanda {order.id}</p>
                                {isFiscalAdmin() && <div className="mt-2 min-w-40 max-w-xs"><FiscalStatus data={fiscalStatuses[order.id]} /></div>}
                              </TableCell>
                              <TableCell className="hidden md:table-cell">
                                <p className="text-xs text-muted-foreground truncate max-w-[200px] whitespace-pre-line leading-tight">
                                  {order.descPedido || "-"}
                                </p>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground hidden sm:table-cell truncate max-w-[100px]">
                                {order.pagamento || "-"}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell">
                                {order.taxa_entrega && order.taxa_entrega !== "0" ? (
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Truck className="w-3 h-3" /> R${order.taxa_entrega}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge className={STATUS_MAP[order.statusPedido]?.cls || "bg-secondary border-transparent"}>
                                  {STATUS_MAP[order.statusPedido]?.label || order.statusPedido || "-"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-sm font-bold text-accent">{fmtBRL(order.Valor_Total)}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="outline" size="sm" className="h-8 gap-1" aria-label={`Editar pedido ${order.id}`} onClick={() => setEditingOrder(order)}>
                                    <Pencil className="w-3.5 h-3.5" /> Editar
                                  </Button>
                                  {isFiscalAdmin() && <Button variant="outline" size="sm" className="h-8" onClick={() => setFiscalOrder(order)}>NFC-e</Button>}
                                  <Button 
                                    title="Imprimir Comanda"
                                    variant="ghost" 
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-primary" 
                                    onClick={() => handlePrint(order)}
                                  >
                                    <Printer className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {editingOrder && <OrderDialog key={editingOrder.pedido_uid || editingOrder.id} open editingOrder={editingOrder}
        onOpenChange={open => { if (!open) setEditingOrder(null); }} onSaved={handleOrderSaved} />}
      {fiscalOrder && <FiscalOrderDialog key={fiscalOrder.id} order={fiscalOrder} onClose={() => setFiscalOrder(null)} onSaved={handleOrderSaved} />}
    </div>
  );
}
